import React, { useCallback, useState } from 'react'
import { shallow } from 'zustand/shallow'
import styled from 'styled-components'
import tokens from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import { Flex, TabMenu, Tab, Button, Text, useMatchBreakpoints } from 'vvs-uikit'
import { Route, useHistory, useLocation } from 'react-router-dom'
import { useWeb3React } from '@web3-react/core'
import Dots from 'components/Loader/Dots'
import { AutoColumn } from 'components/Layout/Column'
import { useUserVvsBoost } from 'state/xVvs/hooks'
import groupBy from 'lodash/groupBy'
import { BIG_ZERO } from 'utils/bigNumber'
import useTheme from 'hooks/useTheme'
import { useVaultsApr } from 'views/xVvs/hooks/useVaultsApr'
import { formatNumber } from 'utils/formatBalance'
import { useUserBoostedFarmsInfos } from 'state/farmBoost/useUserBoostedFarmsInfos'
import { Path } from 'config/constants/paths'
import { StyledCard, Body, OuterBody, StyledCalculateCard } from '../../styles'
import StyledVvsBarCardHeader from '../StyledVvsBarCardHeader'
import BoostBody from './BoostBody'
import EarningsBody from './EarningsBody'
import VaultInnerCard from './VaultInnerCard'
import DepositXvvsButton from './DepositXvvsButton'

const GoldenTag = styled.div`
  padding: 0 8px;
  background-color: ${({ theme }) => (theme.isDark ? theme.colors.legendary : theme.colors.inputSecondary)};
  border-radius: 5px;
`

const RedDot = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background-color: #ff0000;
  border-radius: 50%;
  top: 0;
  right: -12px;
`

const VaultBody: React.FC = () => {
  const { t } = useTranslation()
  const { account } = useWeb3React()

  const { boostData, userDataLoaded } = useUserVvsBoost()
  const vaultsApr = useVaultsApr()
  const earnMaxApr = vaultsApr.length === 0 ? 0 : Math.max(...vaultsApr.map((x) => Number(x.apr)))

  const [showMoreCount, setShowMoreCount] = useState<number>(0)

  const renderBody = () => {
    if (!account) {
      return (
        <Text color="textSubtle" textAlign="center">
          {t('Connect to a wallet to view your vaults.')}
        </Text>
      )
    }
    if (!userDataLoaded) {
      return (
        <Text color="textSubtle" textAlign="center">
          <Dots>{t('Loading')}</Dots>
        </Text>
      )
    }
    if (boostData.stakes.filter((x) => x.active).length === 0) {
      return (
        <>
          <AutoColumn gap="8px">
            <Text bold textAlign="center">
              {t('No Vault found.')}
            </Text>
            <Text color="textSubtle" textAlign="center">
              {t('Deposit your xVVS now and earn up to %apr%% APR', {
                apr: formatNumber(earnMaxApr),
              })}
            </Text>
          </AutoColumn>
        </>
      )
    }

    const grouped = groupBy(
      boostData.stakes
        .map((x, i) => ({
          ...x,
          stakeId: i,
        }))
        .filter((x) => x.active),
      (p) => p.poolId,
    )

    const aggregated = Object.keys(grouped).map((k) =>
      grouped[k]
        .map((x) => ({
          ...x,
          stakeIds: [] as number[],
          withdrawableStakeIds: [] as number[],
        }))
        .reduce(
          (a, b) => {
            const { withdrawableStakeIds, stakeIds } = a
            const isPeriodFulfilled = b.unlockTimestamp <= new Date().getTime() / 1000
            stakeIds.push(b.stakeId)
            if (isPeriodFulfilled) withdrawableStakeIds.push(b.stakeId)
            return {
              ...a,
              amount: a.amount.plus(b.amount),
              totalLocked: a.totalLocked.plus(!isPeriodFulfilled ? b.amount : BIG_ZERO),
              totalUnlocked: a.totalUnlocked.plus(isPeriodFulfilled ? b.amount : BIG_ZERO),
              poolId: b.poolId,
              stakeIds,
              withdrawableStakeIds,
              lastUnlockTimestamp: Math.max(a.lastUnlockTimestamp, b.unlockTimestamp),
            }
          },
          {
            amount: BIG_ZERO,
            totalLocked: BIG_ZERO,
            totalUnlocked: BIG_ZERO,
            poolId: 0,
            stakeTimestamp: 0,
            unlockTimestamp: 0,
            active: false,
            stakeIds: [],
            withdrawableStakeIds: [],
            lastUnlockTimestamp: 0,
          },
        ),
    )

    return aggregated.map((stake) => (
      <VaultInnerCard
        {...stake}
        totalDeposited={stake.amount}
        key={stake.poolId}
        activeVaultNums={aggregated?.length}
        showMoreCallback={(showMore) => {
          setShowMoreCount(showMore ? showMoreCount + 1 : showMoreCount - 1)
        }}
      />
    ))
  }
  return <Body>{renderBody()}</Body>
}

const VaultCard: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { theme, isDark } = useTheme()
  const { isDesktop } = useMatchBreakpoints()

  const { boostData } = useUserVvsBoost()
  const { stakes } = boostData

  const [boostedFarms, isBoostedFarmsLoading] = useUserBoostedFarmsInfos(
    (s) => [s.value.boostedFarmsIds, !s.loaded],
    shallow,
  )

  const activeStakes = stakes?.filter((x) => x.active)

  let activeIndex
  switch (location.pathname) {
    case '/xvvs/vault':
      activeIndex = 0
      break
    case '/xvvs/vault/boost':
      activeIndex = 1
      break
    case '/xvvs/vault/earnings':
      activeIndex = 2
      break
    default:
      activeIndex = -1
      break
  }

  const handleItemClick = (idx: number) => {
    switch (idx) {
      case 0:
        history.push('/xvvs/vault')
        break
      case 1:
        history.push('/xvvs/vault/boost')
        break
      case 2:
        history.push('/xvvs/vault/earnings')
        break
      default:
        history.push('/xvvs/vault')
        break
    }
  }

  const renderContent = useCallback((): JSX.Element => {
    return (
      <>
        <Route exact path="/xvvs/vault" component={VaultBody} />
        <Route exact path="/xvvs/vault/earnings" component={EarningsBody} />
        <Route exact path="/xvvs/vault/boost" component={BoostBody} />
      </>
    )
  }, [])

  return (
    <>
      <StyledCard>
        <StyledVvsBarCardHeader earningToken={tokens.xvvs} stakingToken={tokens.vvs} isVault />
        <OuterBody>
          <TabMenu activeIndex={activeIndex} onItemClick={handleItemClick} variant="altered">
            <Tab>{t('Vault')}</Tab>
            <Tab>
              <Flex gap="8px" alignItems="center" justifyContent="center" position="relative">
                {t('Boost')}

                {activeIndex !== 1 && activeStakes?.length === 0 && (
                  <GoldenTag>
                    <Text
                      lineHeight="20px"
                      fontSize="13px"
                      color={isDark ? theme.colors.black : theme.colors.legendary}
                      fontWeight={700}
                    >
                      {t('NEW!')}
                    </Text>
                  </GoldenTag>
                )}

                {!isBoostedFarmsLoading && activeStakes?.length > 0 && boostedFarms?.length === 0 && <RedDot />}
              </Flex>
            </Tab>
            <Tab>{t('Earnings')}</Tab>
          </TabMenu>
          {renderContent()}
          {activeIndex === 0 && <DepositXvvsButton />}
        </OuterBody>
      </StyledCard>
      {activeIndex === 1 && (
        <StyledCalculateCard>
          <Flex flexDirection="column" gap="4px">
            <Text fontSize="13px" fontWeight="700">
              {t('Simulate locking strategy to maximize boost factor')}
            </Text>
            <Text fontSize="13px" color="textSubtle" fontWeight="400">
              {t('Calculate rewards that you will earn from boost farming.')}
            </Text>
          </Flex>
          <Button width={isDesktop ? 'auto' : '100%'} onClick={() => history.push(Path.BOOST_CALCULATOR)}>
            {t('Calculate')}
          </Button>
        </StyledCalculateCard>
      )}
    </>
  )
}

export default VaultCard
